import { Toggle as BaseToggle } from '@base-ui/react/toggle';
import { ToggleGroup as BaseToggleGroup } from '@base-ui/react/toggle-group';
import { clsx } from 'clsx';
import { createContext, useContext } from 'react';
import { toggleGroupV2, toggleV2 } from './toggle-v2.css.ts';

export type ToggleV2Variant = keyof typeof toggleV2;

const ToggleV2VariantContext = createContext<ToggleV2Variant | null>(null);

export type ToggleGroupV2Props<Value extends string = string> = Omit<
  BaseToggleGroup.Props<Value>,
  'value' | 'defaultValue' | 'onValueChange' | 'multiple'
> & {
  variant: ToggleV2Variant;
  value: Value;
  onValueChange: (value: Value) => void;
};

export const ToggleGroupV2 = <Value extends string>({
  variant,
  value,
  onValueChange,
  className,
  children,
  ...rest
}: ToggleGroupV2Props<Value>) => (
  <ToggleV2VariantContext.Provider value={variant}>
    <BaseToggleGroup
      {...rest}
      className={clsx(toggleGroupV2[variant], className)}
      multiple={false}
      value={[value]}
      onValueChange={(next) => {
        const selected = next[0];
        if (selected == null) {
          return;
        }
        onValueChange(selected);
      }}
    >
      {children}
    </BaseToggleGroup>
  </ToggleV2VariantContext.Provider>
);

export type ToggleV2Props<Value extends string = string> =
  BaseToggle.Props<Value> & {
    variant?: ToggleV2Variant;
  };

export const ToggleV2 = <Value extends string>({
  variant: variantProp,
  className,
  ...rest
}: ToggleV2Props<Value>) => {
  const inherited = useContext(ToggleV2VariantContext);
  const variant = variantProp ?? inherited ?? 'outline';

  return (
    <BaseToggle className={clsx(toggleV2[variant], className)} {...rest} />
  );
};
